/**
 * Panel 6: Intelligence Assessment — W4M consensus with progress tracking
 * Unavailable when DDIL connectivity is DENIED
 */

import { useState, useEffect, useCallback } from 'react'
import type { AssetSummary, EdgeStatus, IntelJob } from '../../../types'
import { getForceComposition, generateIntel, getIntelStatus, fetchApi } from '../../../services/api'

interface IntelAssessmentPanelProps {
  edgeStatus: EdgeStatus | null
}

interface IntelReport {
  job_id: string
  asset_id: string
  report_type: string
  created_at: string
  completed_at: string
  summary: string
  assessment: string
  confidence: number
  recommendations: string[]
  sources: string[]
  consensus_level: string
}

interface CachedReport {
  report: IntelReport
  cachedAt: number // Date.now() timestamp
}

const REPORT_TYPES = ['SITREP', 'OPORD', 'THREATCON', 'LOGISTICS', 'AAR', 'INTEL', 'READINESS', 'COA']
const CACHE_TTL_MS = 10 * 60 * 1000 // 10 minutes

// Module-level cache survives component remounts (tab switching)
const reportCache = new Map<string, CachedReport>()

function getCacheKey(assetId: string, reportType: string): string {
  return `${assetId}:${reportType}`
}

function getCachedReport(assetId: string, reportType: string): CachedReport | null {
  const entry = reportCache.get(getCacheKey(assetId, reportType))
  if (!entry) return null
  if (Date.now() - entry.cachedAt > CACHE_TTL_MS) {
    reportCache.delete(getCacheKey(assetId, reportType))
    return null
  }
  return entry
}

function cacheReport(report: IntelReport): void {
  reportCache.set(getCacheKey(report.asset_id, report.report_type), {
    report,
    cachedAt: Date.now(),
  })
}

// ── Module-level in-flight job tracker (survives tab switches) ──
interface InFlightJob {
  jobId: string
  assetId: string
  reportType: string
  status: 'running' | 'complete' | 'failed'
  progress: number
  error?: string
  pollInterval?: ReturnType<typeof setInterval>
  listeners: Set<() => void>  // notify mounted components
}

const inFlightJobs = new Map<string, InFlightJob>()

function getInFlightKey(assetId: string, reportType: string): string {
  return `${assetId}:${reportType}`
}

function startBackgroundPoll(flight: InFlightJob): void {
  if (flight.pollInterval) return // already polling

  flight.pollInterval = setInterval(async () => {
    try {
      const statusRes = await getIntelStatus(flight.jobId)
      if (!statusRes.data) return

      flight.progress = statusRes.data.progress || 0

      if (statusRes.data.status === 'complete') {
        if (flight.pollInterval) clearInterval(flight.pollInterval)
        flight.pollInterval = undefined
        flight.status = 'complete'

        // Fetch and cache the full report
        const reportRes = await fetchApi<IntelReport>(`/intel/${flight.jobId}`)
        if (reportRes.data) {
          cacheReport(reportRes.data)
        }
        // Notify any mounted component
        flight.listeners.forEach(fn => fn())
      } else if (statusRes.data.status === 'failed') {
        if (flight.pollInterval) clearInterval(flight.pollInterval)
        flight.pollInterval = undefined
        flight.status = 'failed'
        flight.error = statusRes.data.error || 'Intel generation failed'
        flight.listeners.forEach(fn => fn())
      } else {
        // Still running — notify for progress updates
        flight.listeners.forEach(fn => fn())
      }
    } catch {
      // Network error during poll — keep trying
    }
  }, 1500)
}

export function IntelAssessmentPanel({ edgeStatus }: IntelAssessmentPanelProps) {
  const [assets, setAssets] = useState<AssetSummary[]>([])
  const [selectedAsset, setSelectedAsset] = useState<string>('')
  const [selectedType, setSelectedType] = useState('SITREP')
  const [generating, setGenerating] = useState(false)
  const [job, setJob] = useState<IntelJob | null>(null)
  const [report, setReport] = useState<IntelReport | null>(null)
  const [isCached, setIsCached] = useState(false)
  const [cachedAgo, setCachedAgo] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [pipelineStages, setPipelineStages] = useState<('ready' | 'running' | 'complete')[]>(['ready', 'ready', 'ready'])
  const [, forceUpdate] = useState(0) // trigger re-renders from background poll

  useEffect(() => {
    getForceComposition().then(res => {
      if (res.data && res.data.assets.length > 0) {
        setAssets(res.data.assets)
        setSelectedAsset(res.data.assets[0].id)
      }
    })
  }, [])

  // On mount or asset/type change: check for in-flight jobs or cached reports
  useEffect(() => {
    if (!selectedAsset) return
    const flightKey = getInFlightKey(selectedAsset, selectedType)
    const flight = inFlightJobs.get(flightKey)

    if (flight && flight.status === 'running') {
      // Resume watching an in-flight job
      setGenerating(true)
      setError(null)
      setReport(null)
      setIsCached(false)
      setJob({ job_id: flight.jobId, status: 'running', asset_id: flight.assetId, report_type: flight.reportType, created_at: '', progress: flight.progress } as IntelJob)

      // Estimate pipeline stage from progress
      if (flight.progress < 0.35) setPipelineStages(['running', 'ready', 'ready'])
      else if (flight.progress < 0.65) setPipelineStages(['complete', 'running', 'ready'])
      else setPipelineStages(['complete', 'complete', 'running'])

      // Subscribe to updates
      const listener = () => {
        const f = inFlightJobs.get(flightKey)
        if (!f) return
        if (f.status === 'complete') {
          setGenerating(false)
          setPipelineStages(['complete', 'complete', 'complete'])
          const cached = getCachedReport(selectedAsset, selectedType)
          if (cached) {
            setReport(cached.report)
            setIsCached(false)
          }
          inFlightJobs.delete(flightKey)
        } else if (f.status === 'failed') {
          setGenerating(false)
          setPipelineStages(['complete', 'complete', 'complete'])
          setError(f.error || 'Intel generation failed')
          inFlightJobs.delete(flightKey)
        } else {
          // Progress update
          if (f.progress < 0.35) setPipelineStages(['running', 'ready', 'ready'])
          else if (f.progress < 0.65) setPipelineStages(['complete', 'running', 'ready'])
          else setPipelineStages(['complete', 'complete', 'running'])
          forceUpdate(n => n + 1)
        }
      }
      flight.listeners.add(listener)
      return () => { flight.listeners.delete(listener) }
    }

    if (flight && flight.status === 'complete') {
      // Job finished while we were away
      inFlightJobs.delete(flightKey)
    }

    // Check report cache
    const cached = getCachedReport(selectedAsset, selectedType)
    if (cached) {
      setReport(cached.report)
      setIsCached(true)
      const mins = Math.floor((Date.now() - cached.cachedAt) / 60000)
      setCachedAgo(mins < 1 ? 'less than 1 min ago' : `${mins} min ago`)
      setError(null)
      setGenerating(false)
      setPipelineStages(['complete', 'complete', 'complete'])
    } else if (!generating) {
      setReport(null)
      setIsCached(false)
      setCachedAgo('')
      setPipelineStages(['ready', 'ready', 'ready'])
    }
  }, [selectedAsset, selectedType])

  const isDenied = edgeStatus?.connectivity === 'DENIED'

  const handleGenerate = useCallback(async (forceRegen = false) => {
    if (!selectedAsset) return

    // Check cache unless forcing regeneration
    if (!forceRegen) {
      const cached = getCachedReport(selectedAsset, selectedType)
      if (cached) {
        setReport(cached.report)
        setIsCached(true)
        const mins = Math.floor((Date.now() - cached.cachedAt) / 60000)
        setCachedAgo(mins < 1 ? 'less than 1 min ago' : `${mins} min ago`)
        setPipelineStages(['complete', 'complete', 'complete'])
        return
      }
    }

    setGenerating(true)
    setError(null)
    setReport(null)
    setIsCached(false)
    setCachedAgo('')
    setJob(null)
    setPipelineStages(['running', 'ready', 'ready'])

    try {
      const res = await generateIntel(selectedAsset, selectedType)
      if (res.error) throw new Error(res.error)
      if (!res.data) throw new Error('No job data returned')

      setJob(res.data)

      // Register in-flight job at module level so it survives tab switches
      const flightKey = getInFlightKey(selectedAsset, selectedType)
      const flight: InFlightJob = {
        jobId: res.data.job_id,
        assetId: selectedAsset,
        reportType: selectedType,
        status: 'running',
        progress: 0,
        listeners: new Set(),
      }

      // Subscribe this mounted instance
      const listener = () => {
        const f = inFlightJobs.get(flightKey)
        if (!f) return
        if (f.status === 'complete') {
          setGenerating(false)
          setPipelineStages(['complete', 'complete', 'complete'])
          const cached = getCachedReport(selectedAsset, selectedType)
          if (cached) {
            setReport(cached.report)
            setIsCached(false)
          }
          inFlightJobs.delete(flightKey)
        } else if (f.status === 'failed') {
          setGenerating(false)
          setPipelineStages(['complete', 'complete', 'complete'])
          setError(f.error || 'Intel generation failed')
          inFlightJobs.delete(flightKey)
        } else {
          if (f.progress < 0.35) setPipelineStages(['running', 'ready', 'ready'])
          else if (f.progress < 0.65) setPipelineStages(['complete', 'running', 'ready'])
          else setPipelineStages(['complete', 'complete', 'running'])
          forceUpdate(n => n + 1)
        }
      }
      flight.listeners.add(listener)
      inFlightJobs.set(flightKey, flight)

      // Start polling at module level (continues even if component unmounts)
      startBackgroundPoll(flight)

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate intel')
      setGenerating(false)
      setPipelineStages(['ready', 'ready', 'ready'])
    }
  }, [selectedAsset, selectedType])

  if (isDenied) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4">
        <div className="text-6xl opacity-30">!</div>
        <div className="text-lg font-semibold text-term-error">UNAVAILABLE — COMMS DENIED</div>
        <div className="text-sm text-term-textDim text-center max-w-md">
          W4M consensus intelligence assessment requires cloud connectivity.
          Switch to DDIL Level 0 (FULL) or Level 1 (DEGRADED) to access this capability.
        </div>
        <div className="term-box p-3 text-xs text-term-textDim max-w-sm">
          <div className="font-medium text-term-text mb-1">Edge models still active:</div>
          <ul className="space-y-1">
            <li>TRM-C2: Capacity prediction (Panel 9)</li>
            <li>MC-Rate Predictor: Readiness forecasting (Panel 3)</li>
            <li>Tempo Classifier: Operational tempo (Panel 2)</li>
          </ul>
        </div>
      </div>
    )
  }

  const consensusColor = report?.consensus_level === 'unanimous'
    ? 'text-term-success'
    : report?.consensus_level === 'majority'
    ? 'text-term-warning'
    : 'text-term-textDim'

  return (
    <div className="flex flex-col gap-4 h-full overflow-auto">
      <div className="text-xs text-term-textDim font-medium uppercase tracking-wider">
        W4M Consensus Intelligence Assessment
      </div>

      {/* Controls */}
      <div className="flex gap-3 items-end">
        <div className="flex-1">
          <label className="text-[10px] text-term-textDim uppercase block mb-1">Asset</label>
          <select value={selectedAsset} onChange={e => setSelectedAsset(e.target.value)} className="term-input text-sm">
            {assets.map(a => <option key={a.id} value={a.id}>{a.asset_type}</option>)}
          </select>
        </div>
        <div className="flex-1">
          <label className="text-[10px] text-term-textDim uppercase block mb-1">Report Type</label>
          <select value={selectedType} onChange={e => setSelectedType(e.target.value)} className="term-input text-sm">
            {REPORT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <button
          onClick={() => handleGenerate(false)}
          disabled={generating || !selectedAsset}
          className="term-button term-button-primary disabled:opacity-50"
        >
          {generating ? 'Generating...' : isCached ? 'Regenerate' : 'Generate'}
        </button>
      </div>

      {/* Cache indicator */}
      {isCached && report && (
        <div className="flex items-center gap-3 text-xs">
          <span className="text-term-textDim font-mono">
            Cached report — generated {cachedAgo}
          </span>
          <button
            onClick={() => handleGenerate(true)}
            disabled={generating}
            className="text-term-primary hover:text-term-primaryBright underline underline-offset-2 disabled:opacity-50"
          >
            Force regenerate
          </button>
        </div>
      )}

      {/* Pipeline visualization */}
      <div className="term-box p-4">
        <div className="text-xs text-term-textDim font-medium uppercase tracking-wider mb-3">
          3-Model W4M Consensus Pipeline
        </div>
        <div className="grid grid-cols-3 gap-3">
          <PipelineStage name="W4M-SIGINT" provider="W4M Model 1" role="OSINT Gathering" status={pipelineStages[0]} />
          <PipelineStage name="W4M-ANALYSIS" provider="W4M Model 2" role="Quantitative Assessment" status={pipelineStages[1]} />
          <PipelineStage name="W4M-FUSION" provider="W4M Model 3" role="Synthesis + Confidence" status={pipelineStages[2]} />
        </div>

        {/* Progress bar */}
        {generating && job && (() => {
          const flight = inFlightJobs.get(getInFlightKey(selectedAsset, selectedType))
          const pct = (flight?.progress || job.progress || 0) * 100
          return (
            <div className="mt-3">
              <div className="flex items-center justify-between text-[10px] text-term-textDim mb-1">
                <span>Progress</span>
                <span className="tabular-nums">{Math.round(pct)}%</span>
              </div>
              <div className="h-1.5 bg-term-bgDark rounded-full overflow-hidden">
                <div
                  className="h-full bg-term-primary rounded-full transition-all duration-500"
                  style={{ width: `${pct}%` }}
                />
              </div>
            </div>
          )
        })()}
      </div>

      {/* Error */}
      {error && (
        <div className="term-box p-3 border-term-error/50">
          <div className="text-term-error text-sm">{error}</div>
        </div>
      )}

      {/* Report Result */}
      {report && (
        <div className="term-box p-4 flex-1 overflow-auto">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-lg font-bold text-term-text flex items-center gap-2">
                {report.report_type} — {assets.find(a => a.id === report.asset_id)?.asset_type || report.asset_id}
                {isCached && (
                  <span className="text-[10px] font-medium px-1.5 py-0.5 bg-term-bgLight border border-term-border rounded text-term-textDim">
                    CACHED
                  </span>
                )}
              </div>
              <div className="text-xs text-term-textDim font-mono">Job: {report.job_id}</div>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-term-primary tabular-nums">{Math.round(report.confidence * 100)}%</div>
              <div className={`text-[10px] font-medium uppercase ${consensusColor}`}>{report.consensus_level}</div>
            </div>
          </div>

          {/* Summary */}
          <div className="mb-4">
            <div className="text-xs text-term-textDim uppercase mb-1">Summary</div>
            <div className="text-sm text-term-text bg-term-bgLight p-3 rounded whitespace-pre-wrap">{report.summary}</div>
          </div>

          {/* Assessment */}
          {report.assessment !== report.summary && (
            <div className="mb-4">
              <div className="text-xs text-term-textDim uppercase mb-1">Assessment</div>
              <div className="text-sm text-term-text bg-term-bgLight p-3 rounded whitespace-pre-wrap">{report.assessment}</div>
            </div>
          )}

          {/* Recommendations */}
          {report.recommendations.length > 0 && (
            <div className="mb-4">
              <div className="text-xs text-term-textDim uppercase mb-1">Recommendations</div>
              <ul className="space-y-1">
                {report.recommendations.map((r, i) => (
                  <li key={i} className="text-xs text-term-text flex gap-2">
                    <span className="text-term-primary shrink-0">{i + 1}.</span> {r}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Sources */}
          {report.sources.length > 0 && (
            <div>
              <div className="text-xs text-term-textDim uppercase mb-1">Sources ({report.sources.length})</div>
              <div className="space-y-1">
                {report.sources.map((s, i) => (
                  <div key={i} className="text-[10px] text-term-primary truncate font-mono">
                    {s.startsWith('http') ? (
                      <a href={s} target="_blank" rel="noopener noreferrer" className="hover:underline">
                        {s}
                      </a>
                    ) : (
                      s
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Empty state */}
      {!generating && !report && !error && (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center text-term-textDim text-sm">
            <div className="text-4xl opacity-20 mb-3">=</div>
            <div>Select an asset and report type, then click Generate</div>
            <div className="text-xs mt-2 opacity-75">
              8 report types: SITREP, OPORD, THREATCON, LOGISTICS, AAR, INTEL, READINESS, COA
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function PipelineStage({ name, provider, role, status }: { name: string; provider: string; role: string; status: 'ready' | 'running' | 'complete' }) {
  const statusColor = status === 'running' ? 'text-term-warning' : status === 'complete' ? 'text-term-success' : 'text-term-textDim'
  return (
    <div className="term-box p-3">
      <div className="flex items-center gap-2 mb-1">
        <span className={`inline-block w-2 h-2 rounded-full ${status === 'running' ? 'bg-term-warning animate-pulse' : status === 'complete' ? 'bg-term-success' : 'bg-term-border'}`} />
        <span className="text-sm font-bold text-term-text">{name}</span>
      </div>
      <div className="text-[10px] text-term-textDim">{provider}</div>
      <div className="text-[10px] text-term-textDim mt-1">{role}</div>
      <div className={`text-[10px] font-medium mt-1 uppercase ${statusColor}`}>{status}</div>
    </div>
  )
}
